import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useGetRelifDashboardMetricsMutation } from 'services/api/relifDashboard.api';
import { SPANISH_MONTH_NAMES } from 'utils/dateHelpers';

import MonthlyBarChart from './charts/MonthlyBarChart';
import LoadingState from './common/LoadingState';
import StatCard from './StatCard';
import TopBusinessUnitsTable from './TopBusinessUnitsTable';

interface LeadsDashboardProps {
  selectedYear?: number;
  selectedMonth?: number | '';
}

const LeadsDashboard: React.FC<LeadsDashboardProps> = ({
  selectedYear: propSelectedYear,
  selectedMonth: propSelectedMonth,
}) => {
  const currentDate = new Date();
  const activeYear = propSelectedYear ?? currentDate.getFullYear();
  const defaultMonth: number | '' = propSelectedMonth !== undefined ? propSelectedMonth : currentDate.getMonth() + 1;

  const [pendingMonth, setPendingMonth] = useState<number | ''>(defaultMonth);
  const [activeMonth, setActiveMonth] = useState<number | ''>(defaultMonth);

  const theme = useTheme();

  const [getMetrics, { data: metrics, isLoading }] = useGetRelifDashboardMetricsMutation();

  // Fetch when parent year or active month change
  useEffect(() => {
    getMetrics({
      year: activeYear,
      ...(activeMonth !== '' ? { month: activeMonth } : {}),
    }).catch(() => {});
  }, [activeYear, activeMonth, getMetrics]);

  const handleFetchMetrics = () => {
    setActiveMonth(pendingMonth);
    getMetrics({
      year: activeYear,
      ...(pendingMonth !== '' ? { month: pendingMonth } : {}),
    }).catch(() => {});
  };

  const leadsByMonthData = metrics?.charts?.leadsByMonth
    ? Object.entries(metrics.charts.leadsByMonth).map(([label, value]) => ({
        label,
        value,
      }))
    : [];

  const leadsByDayData = metrics?.charts?.leadsByDay
    ? Object.entries(metrics.charts.leadsByDay).map(([label, value]) => ({
        label: label.split('-')[2],
        value,
      }))
    : [];

  const leadsPassedToCRMByDayData = metrics?.charts?.leadsPassedToCRMByDay
    ? Object.entries(metrics.charts.leadsPassedToCRMByDay).map(([label, value]) => ({
        label: label.split('-')[2],
        value,
      }))
    : [];

  const leadsAppointedByDayData = metrics?.charts?.leadsAppointedByDay
    ? Object.entries(metrics.charts.leadsAppointedByDay).map(([label, value]) => ({
        label: label.split('-')[2],
        value,
      }))
    : [];

  const leadsByTypeData = metrics?.charts?.leadsByBusinessUnitType
    ? Object.entries(metrics.charts.leadsByBusinessUnitType).map(([label, value], index) => ({
        id: index,
        label,
        value,
      }))
    : [];

  const pieColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select value={pendingMonth} label="Mes" onChange={(e) => setPendingMonth(e.target.value as number | '')}>
                <MenuItem value="">Todos los meses</MenuItem>
                {SPANISH_MONTH_NAMES.map((month, index) => (
                  <MenuItem key={month} value={index + 1}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              onClick={handleFetchMetrics}
              disabled={isLoading}
              variant="outlined"
              fullWidth
              sx={{ height: '40px' }}
            >
              OBTENER MÉTRICAS
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Leads Anuales"
            value={metrics?.totals?.yearlyLeads || 0}
            subtitle={`Año ${activeYear}`}
            trend={metrics?.totals?.yearOverYearGrowth}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Leads Año Anterior"
            value={metrics?.totals?.previousYearLeads || 0}
            subtitle={`Año ${activeYear - 1}`}
            color="info"
          />
        </Grid>
        {activeMonth !== '' && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={`Leads ${metrics?.monthName || ''}`}
                value={metrics?.totals?.monthlyLeads || 0}
                subtitle={`${metrics?.monthName} ${activeYear}`}
                trend={metrics?.totals?.monthOverMonthGrowth}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Leads Mes Anterior"
                value={metrics?.totals?.previousMonthLeads || 0}
                subtitle="Comparación mensual"
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Leads Pasados a CRM"
                value={metrics?.totals?.monthlyLeadsPassedToCRM || 0}
                subtitle={`${metrics?.monthName} ${activeYear}`}
                percentage={
                  metrics?.totals?.monthlyLeads && metrics?.totals?.monthlyLeads > 0
                    ? ((metrics.totals.monthlyLeadsPassedToCRM || 0) / metrics.totals.monthlyLeads) * 100
                    : undefined
                }
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Leads con Cita"
                value={metrics?.totals?.monthlyLeadsAppointed || 0}
                subtitle={`${metrics?.monthName} ${activeYear}`}
                percentage={
                  metrics?.totals?.monthlyLeads && metrics?.totals?.monthlyLeads > 0
                    ? ((metrics.totals.monthlyLeadsAppointed || 0) / metrics.totals.monthlyLeads) * 100
                    : undefined
                }
                color="warning"
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <MonthlyBarChart title={`Leads por Mes (${activeYear})`} data={leadsByMonthData} />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Leads por Tipo de Cliente - {activeYear}
              </Typography>
              {leadsByTypeData.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: leadsByTypeData.map((d, i) => ({
                        ...d,
                        color: pieColors[i % pieColors.length],
                      })),
                      highlightScope: { fade: 'global', highlight: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30 },
                      innerRadius: 50,
                      paddingAngle: 2,
                      cornerRadius: 5,
                    },
                  ]}
                  height={350}
                  margin={{ top: 20, bottom: 80, left: 20, right: 20 }}
                  slotProps={{
                    legend: {
                      direction: 'horizontal',

                      position: {
                        vertical: 'bottom',
                        horizontal: 'center',
                      },
                    },
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    No hay datos disponibles
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {activeMonth !== '' && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {leadsByDayData.length > 0 && (
            <Grid size={12}>
              <MonthlyBarChart
                title={`Leads por Día - ${metrics?.monthName} ${activeYear}`}
                data={leadsByDayData}
                yAxisLabel="Leads"
                color={theme.palette.secondary.main}
              />
            </Grid>
          )}

          {leadsByDayData.length > 0 && (
            <Grid size={12}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Salud del Servicio - {metrics?.monthName} {activeYear}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 3,
                    }}
                  >
                    Monitoreo diario de leads pasados a CRM y leads con cita para detectar problemas de servicio
                  </Typography>
                  <LineChart
                    series={[
                      {
                        data: leadsByDayData.map((dayItem) => {
                          const matchingItem = leadsPassedToCRMByDayData.find((item) => item.label === dayItem.label);
                          return matchingItem?.value || 0;
                        }),

                        color: theme.palette.success.main,
                        label: 'Leads Pasados a CRM',
                        showMark: true,
                      },
                      {
                        data: leadsByDayData.map((dayItem) => {
                          const matchingItem = leadsAppointedByDayData.find((item) => item.label === dayItem.label);
                          return matchingItem?.value || 0;
                        }),

                        color: theme.palette.info.main,
                        label: 'Leads con Cita',
                        showMark: true,
                      },
                    ]}
                    xAxis={[
                      {
                        data: leadsByDayData.map((d) => d.label),
                        scaleType: 'band',
                      },
                    ]}
                    yAxis={[
                      {
                        label: 'Leads',
                      },
                    ]}
                    height={350}
                    margin={{ top: 60, bottom: 40, left: 60, right: 80 }}
                    slotProps={{
                      legend: {
                        position: {
                          vertical: 'top',
                          horizontal: 'end',
                        },
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopBusinessUnitsTable
            data={metrics?.topBusinessUnits?.yearly || []}
            title={`Top 10 Clientes - ${activeYear}`}
          />
        </Grid>
        {activeMonth !== '' && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TopBusinessUnitsTable
              data={metrics?.topBusinessUnits?.monthly || []}
              title={`Top 10 Clientes - ${metrics?.monthName} ${activeYear}`}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LeadsDashboard;
